import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, API } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../components/ui/alert-dialog';
import {
  LogOut, Loader2, Search, FlaskConical, Building2, FileText,
  Power, PowerOff, Trash2, Eye, MapPin, Activity, Users, ShieldAlert,
  Inbox, Archive, ArchiveRestore, CheckCircle2, XCircle, Edit, Download, Bell,
  KeyRound, ShieldCheck, UserCog, ChevronLeft, Flame, Skull, Droplet,
  AlertTriangle, Leaf, Bomb, Tv, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import SiteFooter from '../components/SiteFooter';
import ModonLogo from '../components/ModonLogo';
import './AdminDashboard.modon.css';
import LanguageSwitcher from '../components/LanguageSwitcher';

const RISK_COLORS = { high: '#ef4444', medium: '#f59e0b', low: '#10b981' };

function Stat({ icon: Icon, label, value, color = 'bg-[#1a4d80]', testid, onClick }) {
  const clickable = !!onClick;
  return (
    <Card
      onClick={onClick}
      className={`relative p-4 flex items-center gap-3 bg-gradient-to-br from-white to-slate-50 border-slate-200/70 shadow-[0_8px_24px_-8px_rgba(15,23,42,0.18),0_2px_6px_-2px_rgba(15,23,42,0.08)] hover:shadow-[0_18px_36px_-10px_rgba(15,23,42,0.28),0_6px_14px_-4px_rgba(15,23,42,0.12)] hover:-translate-y-1 transition-all duration-300 ease-out overflow-hidden group ${clickable ? 'cursor-pointer' : ''}`}
      data-testid={testid}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent" />
      <div className={`${color} text-white rounded-xl w-12 h-12 flex items-center justify-center shadow-[inset_0_-3px_0_rgba(0,0,0,0.18),0_8px_18px_-4px_rgba(15,23,42,0.35)] group-hover:scale-105 transition-transform duration-300`}>
        <Icon size={22} className="drop-shadow-[0_2px_2px_rgba(0,0,0,0.25)]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-slate-500 font-medium">{label}</div>
        <div className="text-2xl font-extrabold text-slate-900 drop-shadow-sm">{value}</div>
      </div>
      {clickable && (
        <ChevronLeft
          size={18}
          className="text-slate-300 group-hover:text-[#1a4d80] group-hover:-translate-x-0.5 transition-all"
        />
      )}
    </Card>
  );
}

function RiskPill({ risk }) {
  if (!risk) return null;
  const c = RISK_COLORS[risk.level] || '#64748b';
  return (
    <Badge style={{ backgroundColor: c, color: 'white' }}>
      <ShieldAlert size={10} className="ml-1"/> {risk.labelAr} ({risk.score})
    </Badge>
  );
}

function BarsList({ items, accent = 'bg-[#1a4d80]', total }) {
  const list = (items || []).slice(0, 7);
  const max = Math.max(1, ...list.map((i) => i.count || 0));
  if (list.length === 0) return <div className="text-xs text-slate-500 py-3 text-center">لا توجد بيانات</div>;
  return (
    <div className="space-y-1.5">
      {list.map((it) => {
        const pct = Math.round((it.count / max) * 100);
        const ofTotal = total ? Math.round((it.count / total) * 100) : null;
        return (
          <div key={it.name} className="text-xs">
            <div className="flex justify-between mb-0.5">
              <span className="font-semibold text-slate-800 truncate">{it.name}</span>
              <span className="text-slate-500 font-bold">{it.count}{ofTotal !== null ? ` • ${ofTotal}%` : ''}</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${accent} transition-all`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminDashboard() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const [tab, setTab] = useState('factories');
  const [stats, setStats] = useState(null);
  const [notif, setNotif] = useState(null);
  const [factories, setFactories] = useState([]);
  const [sdsList, setSdsList] = useState([]);
  const [archivedList, setArchivedList] = useState([]);
  const [deletionReqs, setDeletionReqs] = useState([]);
  const [fLoading, setFLoading] = useState(false);
  const [sLoading, setSLoading] = useState(false);

  const [fQuery, setFQuery] = useState('');
  const [fCity, setFCity] = useState('');
  const [sQuery, setSQuery] = useState('');
  const [sCity, setSCity] = useState('');
  const [sRisk, setSRisk] = useState('any');

  const [detailFactory, setDetailFactory] = useState(null);
  const [detailSDS, setDetailSDS] = useState(null);

  // Password reset dialog
  const [pwdResetUser, setPwdResetUser] = useState(null); // { id, label }
  const [pwdValue, setPwdValue] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdSubmitting, setPwdSubmitting] = useState(false);

  // Role management dialog
  const [roleMgrOpen, setRoleMgrOpen] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [roleLoading, setRoleLoading] = useState(false);

  // Expanded KPI dialog
  const [expandedStat, setExpandedStat] = useState(null); // 'factories' | 'active' | 'sds' | 'chemicals' | 'risk-high' | 'risk-medium' | 'risk-low'

  // Public TV dashboard link toggle
  const [tvEnabled, setTvEnabled] = useState(true);
  const loadTvStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/tv-status`);
      setTvEnabled(!!r.data?.enabled);
    } catch (e) { console.warn('[AdminDashboard] loadTvStatus failed', e); }
  };
  const toggleTv = async (next) => {
    setTvEnabled(next); // optimistic
    try {
      await axios.patch(`${API}/admin/tv-status`, { enabled: next });
      toast.success(next ? t('tv.linkEnabled') : t('tv.linkDisabled'));
    } catch {
      setTvEnabled(!next);
      toast.error(t('admin.actionFailed', 'تعذر تنفيذ العملية'));
    }
  };
  const copyTvLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/admin/tv`);
      toast.success(t('tv.linkCopied'));
    } catch (e) { console.warn('[AdminDashboard] copy link failed', e); }
  };

  // Confirmation dialog (replaces native window.confirm which can be blocked in iframe/preview)
  const [confirmState, setConfirmState] = useState(null); // { title, description, confirmLabel, onConfirm, danger }
  const askConfirm = (opts) => setConfirmState(opts);
  const closeConfirm = () => setConfirmState(null);

  const loadAll = async () => {
    try {
      const [s, n, dr, arc] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/notifications`),
        axios.get(`${API}/admin/deletion-requests`),
        axios.get(`${API}/admin/sds`, { params: { archived: true } }),
      ]);
      setStats(s.data);
      setNotif(n.data);
      setDeletionReqs(dr.data || []);
      setArchivedList(arc.data || []);
    } catch (e) {
      // Aggregated load (stats + notifications + deletion reqs + archive).
      // Don't block UI on a single failure, but log so we notice outages.
      console.warn('[AdminDashboard] loadAll partial failure', e);
    }
  };

  const loadFactories = async () => {
    setFLoading(true);
    try {
      const params = {};
      if (fQuery) params.q = fQuery;
      if (fCity) params.city = fCity;
      const r = await axios.get(`${API}/admin/factories`, { params });
      setFactories(r.data || []);
    } catch { toast.error('تعذر تحميل قائمة المصانع'); }
    finally { setFLoading(false); }
  };

  const loadSDS = async () => {
    setSLoading(true);
    try {
      const params = {};
      if (sQuery) params.q = sQuery;
      if (sCity) params.city = sCity;
      if (sRisk && sRisk !== 'any') params.risk = sRisk;
      const r = await axios.get(`${API}/admin/sds`, { params });
      setSdsList(r.data || []);
    } catch { toast.error('تعذر تحميل قائمة التقارير'); }
    finally { setSLoading(false); }
  };

  useEffect(() => { loadAll(); loadFactories(); loadSDS(); loadTvStatus(); }, []);

  const toggleFactory = async (f) => {
    try {
      const r = await axios.patch(`${API}/admin/factories/${f.id}/toggle`);
      setFactories((p) => p.map((x) => x.id === f.id ? { ...x, is_active: r.data.is_active } : x));
      toast.success(r.data.is_active ? 'تم تفعيل المصنع' : 'تم تعطيل المصنع');
    } catch { toast.error('تعذر تغيير الحالة'); }
  };
  const deleteFactory = async (f) => {
    askConfirm({
      title: 'حذف المصنع نهائياً',
      description: `سيتم حذف "${f.factoryName}" وجميع تقاريره. هذا الإجراء لا يمكن التراجع عنه.`,
      confirmLabel: 'نعم، احذف',
      danger: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/admin/factories/${f.id}`);
          setFactories((p) => p.filter((x) => x.id !== f.id));
          toast.success('تم حذف المصنع');
          loadAll();
        } catch { toast.error('تعذر الحذف'); }
      },
    });
  };

  const archiveSDS = async (s) => {
    try {
      await axios.post(`${API}/admin/sds/${s.id}/archive`);
      toast.success('تم الأرشفة');
      loadSDS(); loadAll();
    } catch { toast.error('تعذر الأرشفة'); }
  };
  const restoreSDS = async (s) => {
    try {
      await axios.post(`${API}/admin/sds/${s.id}/restore`);
      toast.success('تم الاستعادة');
      loadAll(); loadSDS();
    } catch { toast.error('تعذر الاستعادة'); }
  };
  const hardDeleteSDS = async (s) => {
    askConfirm({
      title: 'حذف نهائي للتقرير',
      description: `سيتم حذف التقرير "${s.title || 'تقرير SDS'}" نهائياً. هذا الإجراء لا يمكن التراجع عنه.`,
      confirmLabel: 'نعم، احذف نهائياً',
      danger: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/admin/sds/${s.id}`);
          toast.success('تم الحذف');
          loadAll(); loadSDS();
        } catch (e) {
          const msg = e?.response?.data?.detail || 'تعذر الحذف';
          toast.error(msg);
        }
      },
    });
  };

  const approveDel = async (s) => {
    try {
      await axios.post(`${API}/admin/deletion-requests/${s.id}/approve`);
      toast.success('تمت الموافقة وأُرشف التقرير');
      loadAll(); loadSDS();
    } catch { toast.error('تعذر التنفيذ'); }
  };
  const rejectDel = async (s) => {
    try {
      await axios.post(`${API}/admin/deletion-requests/${s.id}/reject`);
      toast.success('تم رفض الطلب');
      loadAll(); loadSDS();
    } catch { toast.error('تعذر التنفيذ'); }
  };

  const openFactoryDetail = async (f) => {
    try {
      const r = await axios.get(`${API}/admin/factories/${f.id}`);
      setDetailFactory(r.data);
    } catch { toast.error('تعذر تحميل التفاصيل'); }
  };
  const openSDSDetail = async (s) => {
    try {
      const r = await axios.get(`${API}/admin/sds/${s.id}`);
      setDetailSDS(r.data);
    } catch { toast.error('تعذر تحميل التقرير'); }
  };

  const submitPasswordReset = async () => {
    if (!pwdResetUser) return;
    if (pwdValue.length < 6) { toast.error('كلمة المرور يجب ألا تقل عن 6 أحرف'); return; }
    if (pwdValue !== pwdConfirm) { toast.error('كلمتا المرور غير متطابقتين'); return; }
    setPwdSubmitting(true);
    try {
      await axios.post(`${API}/admin/users/${pwdResetUser.id}/reset-password`, { new_password: pwdValue });
      toast.success(`تم تعيين كلمة المرور لـ ${pwdResetUser.label}`);
      setPwdResetUser(null); setPwdValue(''); setPwdConfirm('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'تعذر تعيين كلمة المرور');
    } finally { setPwdSubmitting(false); }
  };

  const openRoleManager = async () => {
    setRoleMgrOpen(true);
    setRoleLoading(true);
    try {
      const r = await axios.get(`${API}/admin/admins`);
      setAdminUsers(r.data || []);
    } catch { toast.error('تعذر تحميل قائمة المشرفين'); }
    finally { setRoleLoading(false); }
  };

  const promoteToAdmin = async (f) => {
    askConfirm({
      title: 'ترقية إلى مشرف',
      description: `ترقية "${f.factoryName || f.email}" إلى مشرف؟ سيحصل على كامل صلاحيات الإدارة.`,
      confirmLabel: 'نعم، رقّ',
      onConfirm: async () => {
        try {
          await axios.patch(`${API}/admin/users/${f.id}/role`, { role: 'admin' });
          toast.success('تمت الترقية إلى مشرف');
          loadFactories();
          if (roleMgrOpen) openRoleManager();
        } catch (e) { toast.error(e?.response?.data?.detail || 'تعذر تغيير الدور'); }
      },
    });
  };

  const demoteToFactory = async (u) => {
    askConfirm({
      title: 'تنزيل من مشرف',
      description: `تنزيل "${u.email}" من مشرف إلى حساب مصنع؟`,
      confirmLabel: 'نعم، نزّل',
      danger: true,
      onConfirm: async () => {
        try {
          await axios.patch(`${API}/admin/users/${u.id}/role`, { role: 'factory' });
          toast.success('تم تنزيل الدور');
          if (roleMgrOpen) openRoleManager();
          loadFactories();
        } catch (e) { toast.error(e?.response?.data?.detail || 'تعذر تغيير الدور'); }
      },
    });
  };

  const topCities = useMemo(() => (stats?.cities || []).slice(0, 6), [stats]);
  const topActivities = useMemo(() => (stats?.activities || []).slice(0, 6), [stats]);
  const pendingCount = deletionReqs.length;

  return (
    <div className="admin-modon-shell min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-slate-100 flex flex-col" dir={i18n.language === 'en' ? 'ltr' : 'rtl'}>
      <header className="admin-modon-header bg-white/80 backdrop-blur-md border-b border-slate-200/60 shadow-[0_4px_18px_-6px_rgba(15,23,42,0.18)] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ModonLogo size={44}/>
            <div>
              <h1 className="font-extrabold text-slate-900 leading-tight">{t('admin.title')}</h1>
              <p className="text-xs text-slate-500">{user?.email} • {t('admin.role')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher/>
            <Button size="sm" variant="outline" onClick={openRoleManager} data-testid="open-role-manager-btn">
              <ShieldCheck size={14} className="mx-1"/> {t('admin.manageAdmins')}
            </Button>
            <div className="flex items-center gap-1.5 rounded-lg border border-[#c9a14a]/40 bg-[#c9a14a]/5 pl-1 pr-2 py-1">
              <Link to="/admin/tv"><Button size="sm" className="bg-[#c9a14a] hover:bg-[#a68431] text-white h-7" data-testid="open-executive-tv-btn"><Tv size={14} className="mx-1"/> {t('tv.openBtn')}</Button></Link>
              <Switch checked={tvEnabled} onCheckedChange={toggleTv} data-testid="tv-public-toggle" title={tvEnabled ? t('tv.linkEnabled') : t('tv.linkDisabled')} />
              <button onClick={copyTvLink} className="text-slate-500 hover:text-[#1a4d80] p-1" data-testid="copy-tv-link-btn" title={t('tv.copyLink')}>
                <Copy size={15} />
              </button>
            </div>
            <Link to="/admin/stations">
              <Button
                size="sm"
                className="bg-[#1a4d80] hover:bg-[#143d68] text-white"
                data-testid="admin-stations-btn"
              >
                <Building2 size={14} className="mx-1"/>
                إدارة المحطات
              </Button>
            </Link>

            <Link to="/dashboard"><Button size="sm" variant="outline">{t('admin.publicStats')}</Button></Link>
            <Link to="/map"><Button size="sm" variant="outline">{t('admin.map')}</Button></Link>
            <Button size="sm" variant="outline" onClick={() => { logout(); nav('/login'); }} data-testid="admin-logout-btn">
              <LogOut size={14} className="mx-1" /> {t('common.logout')}
            </Button>
          </div>
        </div>
      </header>

      <main className="admin-modon-main flex-1 max-w-7xl w-full mx-auto px-4 py-6 space-y-6">
        {/* Notifications strip */}
        {notif && (notif.stale.length > 0 || notif.highRisk.length > 0 || pendingCount > 0) && (
          <Card className="p-3 bg-gradient-to-l from-amber-50 to-yellow-50 border-amber-200/70 flex flex-wrap items-center gap-4 text-sm shadow-[0_8px_22px_-8px_rgba(180,83,9,0.22)]">
            <Bell className="text-amber-700 drop-shadow-sm" size={16}/>
            {pendingCount > 0 && (
              <button onClick={() => setTab('requests')} className="text-amber-900 hover:underline font-semibold" data-testid="notif-pending-delete">
                <b>{pendingCount}</b> {t('admin.tabs.requests')}
              </button>
            )}
            {notif.stale.length > 0 && (
              <span className="text-amber-900"><b>{notif.stale.length}</b></span>
            )}
            {notif.highRisk.length > 0 && (
              <span className="text-red-700 font-semibold">{t('admin.notifHighRisk', { count: notif.highRisk.length })}</span>
            )}
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat icon={Building2} label={t('admin.stats.totalFactories')} value={stats?.totalFactories ?? '—'} testid="stat-total-factories" onClick={() => setExpandedStat('factories')}/>
          <Stat icon={Power} label={t('admin.stats.activeFactories')} value={stats?.activeFactories ?? '—'} color="bg-emerald-600" testid="stat-active" onClick={() => setExpandedStat('active')}/>
          <Stat icon={FileText} label={t('admin.stats.totalSDS')} value={stats?.totalSDS ?? '—'} color="bg-indigo-600" testid="stat-sds" onClick={() => setExpandedStat('sds')}/>
          <Stat icon={FlaskConical} label={t('admin.stats.totalChemicals')} value={stats?.totalChemicals ?? '—'} color="bg-amber-600" testid="stat-chemicals" onClick={() => setExpandedStat('chemicals')}/>
        </div>

        {stats?.riskBuckets && (
          <div className="grid grid-cols-3 gap-3">
            <Stat icon={ShieldAlert} label={t('admin.stats.highRisk')} value={stats.riskBuckets.high} color="bg-red-600" testid="stat-risk-high" onClick={() => setExpandedStat('risk-high')}/>
            <Stat icon={ShieldAlert} label={t('admin.stats.mediumRisk')} value={stats.riskBuckets.medium} color="bg-amber-500" onClick={() => setExpandedStat('risk-medium')}/>
            <Stat icon={ShieldAlert} label={t('admin.stats.lowRisk')} value={stats.riskBuckets.low} color="bg-emerald-600" onClick={() => setExpandedStat('risk-low')}/>
          </div>
        )}

        {stats && (
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-5 bg-gradient-to-br from-white to-slate-50/50 border-slate-200/70 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.18),0_3px_8px_-3px_rgba(15,23,42,0.08)] hover:shadow-[0_20px_40px_-12px_rgba(15,23,42,0.22)] transition-shadow duration-300">
              <div className="flex items-center gap-2 mb-3 text-slate-800 font-bold"><MapPin size={16} className="text-[#1a4d80]"/> {t('admin.byCity')}</div>
              <div className="space-y-1">
                {topCities.length === 0 && <div className="text-xs text-slate-500">لا توجد بيانات</div>}
                {topCities.map((c) => (
                  <div key={c.name} className="flex items-center justify-between text-sm border-b last:border-0 py-1.5">
                    <span className="text-slate-700">{c.name}</span>
                    <Badge variant="secondary" className="shadow-sm">{c.count}</Badge>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-5 bg-gradient-to-br from-white to-slate-50/50 border-slate-200/70 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.18),0_3px_8px_-3px_rgba(15,23,42,0.08)] hover:shadow-[0_20px_40px_-12px_rgba(15,23,42,0.22)] transition-shadow duration-300">
              <div className="flex items-center gap-2 mb-3 text-slate-800 font-bold"><Activity size={16} className="text-[#1a4d80]"/> {t('admin.byActivity')}</div>
              <div className="space-y-1">
                {topActivities.length === 0 && <div className="text-xs text-slate-500">لا توجد بيانات</div>}
                {topActivities.map((c) => (
                  <div key={c.name} className="flex items-center justify-between text-sm border-b last:border-0 py-1.5">
                    <span className="text-slate-700">{c.name}</span>
                    <Badge variant="secondary" className="shadow-sm">{c.count}</Badge>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white/80 backdrop-blur-sm shadow-[0_6px_18px_-6px_rgba(15,23,42,0.15)] border border-slate-200/70 p-1 rounded-xl">
            <TabsTrigger value="factories" data-testid="tab-factories"><Users size={14} className="mx-1"/> {t('admin.tabs.factories')}</TabsTrigger>
            <TabsTrigger value="sds" data-testid="tab-sds"><FileText size={14} className="mx-1"/> {t('admin.tabs.sds')}</TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests">
              <Inbox size={14} className="mx-1"/> {t('admin.tabs.requests')}
              {pendingCount > 0 && <Badge className="mx-1 bg-red-600 text-white text-[10px]">{pendingCount}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="archive" data-testid="tab-archive"><Archive size={14} className="mx-1"/> {t('admin.tabs.archive')}</TabsTrigger>
          </TabsList>

          <TabsContent value="factories">
            <Card className="p-5 bg-gradient-to-br from-white to-slate-50/50 border-slate-200/70 shadow-[0_12px_32px_-12px_rgba(15,23,42,0.2),0_4px_10px_-3px_rgba(15,23,42,0.08)]">
              <div className="flex flex-col md:flex-row gap-2 mb-4">
                <div className="relative flex-1">
                  <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                  <Input placeholder={t('admin.searchPlaceholder')} value={fQuery} onChange={(e) => setFQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadFactories()} className="pr-9" data-testid="factory-search-input"/>
                </div>
                <Input placeholder={t('admin.table.city')} value={fCity} onChange={(e) => setFCity(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadFactories()} className="md:w-48" data-testid="factory-city-input"/>
                <Button onClick={loadFactories} className="bg-[#1a4d80] hover:bg-[#143d68]" data-testid="factory-search-btn"><Search size={14} className="mx-1"/> {t('common.search')}</Button>
              </div>
              {fLoading ? (
                <div className="flex items-center gap-2 text-slate-600 py-6"><Loader2 className="animate-spin" size={16}/> جارٍ التحميل...</div>
              ) : factories.length === 0 ? (
                <div className="text-center text-slate-500 py-10">لا توجد مصانع مطابقة</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" dir="rtl">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="text-right p-2">{t('admin.table.factory')}</th>
                        <th className="text-right p-2">{t('admin.table.owner')}</th>
                        <th className="text-right p-2">{t('admin.table.email')}</th>
                        <th className="text-right p-2">{t('admin.table.city')}</th>
                        <th className="text-right p-2">{t('admin.table.activity')}</th>
                        <th className="text-right p-2">{t('admin.table.reports')}</th>
                        <th className="text-right p-2">{t('admin.table.topRisk')}</th>
                        <th className="text-right p-2">{t('admin.table.status')}</th>
                        <th className="text-right p-2">{t('admin.table.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {factories.map((f) => (
                        <tr key={f.id} className="border-t hover:bg-slate-50" data-testid={`factory-row-${f.id}`}>
                          <td className="p-2 font-bold text-slate-900">{f.factoryName}</td>
                          <td className="p-2 text-slate-700">{f.ownerName}</td>
                          <td className="p-2 text-slate-700 text-xs">{f.email}</td>
                          <td className="p-2 text-slate-700">{f.city || '—'}</td>
                          <td className="p-2 text-slate-700">{f.activity || '—'}</td>
                          <td className="p-2"><Badge variant="secondary">{f.sdsCount}</Badge></td>
                          <td className="p-2">
                            {f.sdsCount > 0 ? <Badge style={{ backgroundColor: RISK_COLORS[f.riskLevel] || '#64748b', color: 'white' }}>{f.riskScore}</Badge> : <span className="text-xs text-slate-400">—</span>}
                          </td>
                          <td className="p-2">
                            {f.is_active ? <Badge className="bg-emerald-600">{t('common.active')}</Badge> : <Badge variant="destructive">{t('common.inactive')}</Badge>}
                          </td>
                          <td className="p-2">
                            <div className="flex gap-1 flex-wrap">
                              <Button size="sm" variant="outline" onClick={() => openFactoryDetail(f)} data-testid={`view-factory-${f.id}`} title="عرض تقارير PDF"><Eye size={14}/></Button>
                              <Button size="sm" variant="outline" onClick={() => toggleFactory(f)} data-testid={`toggle-factory-${f.id}`} title={f.is_active ? 'تعطيل' : 'تفعيل'}>
                                {f.is_active ? <PowerOff size={14} className="text-red-600"/> : <Power size={14} className="text-emerald-600"/>}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => { setPwdResetUser({ id: f.id, label: f.factoryName || f.email }); setPwdValue(''); setPwdConfirm(''); }} className="text-amber-700" data-testid={`reset-password-${f.id}`} title="إعادة تعيين كلمة المرور">
                                <KeyRound size={14}/>
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => promoteToAdmin(f)} className="text-indigo-700" data-testid={`promote-admin-${f.id}`} title="ترقية إلى مشرف">
                                <ShieldCheck size={14}/>
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => deleteFactory(f)} className="text-red-600" data-testid={`delete-factory-${f.id}`} title="حذف المصنع"><Trash2 size={14}/></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="sds">
            <Card className="p-5 bg-gradient-to-br from-white to-slate-50/50 border-slate-200/70 shadow-[0_12px_32px_-12px_rgba(15,23,42,0.2),0_4px_10px_-3px_rgba(15,23,42,0.08)]">
              <div className="flex flex-col md:flex-row gap-2 mb-4">
                <div className="relative flex-1">
                  <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                  <Input placeholder={t('admin.searchPlaceholder')} value={sQuery} onChange={(e) => setSQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadSDS()} className="pr-9" data-testid="sds-search-input"/>
                </div>
                <Input placeholder={t('admin.table.city')} value={sCity} onChange={(e) => setSCity(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadSDS()} className="md:w-40" data-testid="sds-city-input"/>
                <Select value={sRisk} onValueChange={setSRisk}>
                  <SelectTrigger className="md:w-40" data-testid="sds-risk-select"><SelectValue placeholder="مستوى الخطر"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">كل المستويات</SelectItem>
                    <SelectItem value="high">عالي</SelectItem>
                    <SelectItem value="medium">متوسط</SelectItem>
                    <SelectItem value="low">منخفض</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={loadSDS} className="bg-[#1a4d80] hover:bg-[#143d68]" data-testid="sds-search-btn"><Search size={14} className="mx-1"/> {t('common.search')}</Button>
              </div>
              {sLoading ? (
                <div className="flex items-center gap-2 text-slate-600 py-6"><Loader2 className="animate-spin" size={16}/> جارٍ التحميل...</div>
              ) : sdsList.length === 0 ? (
                <div className="text-center text-slate-500 py-10">لا توجد تقارير مطابقة</div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {sdsList.map((s) => (
                    <Card key={s.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`sds-card-${s.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="text-[#1a4d80] flex-shrink-0" size={18}/>
                          <h3 className="font-bold text-slate-900 truncate">{s.title}</h3>
                        </div>
                        <RiskPill risk={s.risk}/>
                      </div>
                      <div className="text-xs text-slate-600 space-y-0.5 mb-3">
                        <div><Building2 size={11} className="inline ml-1"/> {s.factoryName || '—'}</div>
                        <div><MapPin size={11} className="inline ml-1"/> {s.factoryCity || '—'}</div>
                        <div>{s.chemicalsCount} مادة كيميائية</div>
                        {s.chemicals?.length > 0 && (
                          <div className="flex flex-wrap gap-1 pt-1">
                            {s.chemicals.slice(0, 4).map((c, i) => (
                              <Badge key={i} variant="outline" className="text-[10px]">{c.name}{c.un ? ` • UN${c.un}` : ''}{c.cas ? ` • CAS${c.cas}` : ''}</Badge>
                            ))}
                          </div>
                        )}
                        {s.deletion_requested && (
                          <Badge variant="outline" className="text-amber-700 border-amber-400 text-[10px]">طلب حذف بانتظار</Badge>
                        )}
                        <div className="pt-1">آخر تعديل: {s.updated_at ? new Date(s.updated_at).toLocaleString('ar-SA') : '—'}</div>
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        <Link to={`/admin/sds/pdf/${s.id}`}>
                          <Button size="sm" className="text-xs bg-[#1a4d80] hover:bg-[#143d68] text-white" data-testid={`admin-view-pdf-${s.id}`}>
                            <FileText size={12} className="ml-1"/> PDF
                          </Button>
                        </Link>
                        <Button size="sm" variant="outline" className="text-xs" onClick={() => openSDSDetail(s)} data-testid={`view-sds-${s.id}`}><Eye size={12} className="ml-1"/> عرض</Button>
                        <Link to={`/admin/sds/edit/${s.id}`}>
                          <Button size="sm" variant="outline" className="text-xs" data-testid={`edit-sds-${s.id}`}><Edit size={12} className="ml-1"/> تعديل</Button>
                        </Link>
                        <Button size="sm" variant="outline" onClick={() => archiveSDS(s)} className="text-amber-700 text-xs" data-testid={`archive-sds-${s.id}`}><Archive size={12}/></Button>
                        <Button size="sm" variant="outline" onClick={() => hardDeleteSDS(s)} className="text-red-600 text-xs" data-testid={`hard-delete-sds-${s.id}`}><Trash2 size={12}/></Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="requests">
            <Card className="p-5 bg-gradient-to-br from-white to-slate-50/50 border-slate-200/70 shadow-[0_12px_32px_-12px_rgba(15,23,42,0.2),0_4px_10px_-3px_rgba(15,23,42,0.08)]">
              {deletionReqs.length === 0 ? (
                <div className="text-center text-slate-500 py-10">لا توجد طلبات حذف بانتظار المراجعة</div>
              ) : (
                <div className="space-y-2">
                  {deletionReqs.map((r) => (
                    <Card key={r.id} className="p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid={`del-req-${r.id}`}>
                      <div>
                        <div className="font-bold text-slate-900">{r.title} <span className="text-xs text-slate-500">({r.factoryName})</span></div>
                        <div className="text-xs text-slate-600">السبب: {r.reason || '—'}</div>
                        <div className="text-xs text-slate-500">{r.chemicalsCount} مواد • تاريخ الطلب: {r.requested_at ? new Date(r.requested_at).toLocaleString('ar-SA') : '—'}</div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => approveDel(r)} data-testid={`approve-del-${r.id}`}>
                          <CheckCircle2 size={14} className="ml-1"/> موافقة (أرشفة)
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => rejectDel(r)} className="text-red-600" data-testid={`reject-del-${r.id}`}>
                          <XCircle size={14} className="ml-1"/> رفض
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="archive">
            <Card className="p-5 bg-gradient-to-br from-white to-slate-50/50 border-slate-200/70 shadow-[0_12px_32px_-12px_rgba(15,23,42,0.2),0_4px_10px_-3px_rgba(15,23,42,0.08)]">
              {archivedList.length === 0 ? (
                <div className="text-center text-slate-500 py-10">لا توجد تقارير مؤرشفة</div>
              ) : (
                <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                  {archivedList.map((s) => (
                    <Card key={s.id} className="p-3" data-testid={`archive-card-${s.id}`}>
                      <div className="font-bold text-slate-900 text-sm">{s.title}</div>
                      <div className="text-xs text-slate-500">{s.factoryName} • {s.factoryCity}</div>
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" variant="outline" onClick={() => restoreSDS(s)} className="text-emerald-700 text-xs" data-testid={`restore-${s.id}`}>
                          <ArchiveRestore size={12} className="ml-1"/> استعادة
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => hardDeleteSDS(s)} className="text-red-600 text-xs" data-testid={`hard-delete-archive-${s.id}`}>
                          <Trash2 size={12} className="ml-1"/> حذف نهائي
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Factory Detail Dialog */}
      <Dialog open={!!detailFactory} onOpenChange={(o) => !o && setDetailFactory(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle>تفاصيل المصنع</DialogTitle>
            <DialogDescription>بيانات الحساب وقائمة التقارير الخاصة بهذا المصنع.</DialogDescription>
          </DialogHeader>
          {detailFactory && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-slate-500">الاسم:</span> <b>{detailFactory.factoryName}</b></div>
                <div><span className="text-slate-500">المالك:</span> {detailFactory.ownerName}</div>
                <div><span className="text-slate-500">البريد:</span> {detailFactory.email}</div>
                <div><span className="text-slate-500">الهاتف:</span> {detailFactory.phone}</div>
                <div><span className="text-slate-500">المدينة:</span> {detailFactory.city}</div>
                <div><span className="text-slate-500">النشاط:</span> {detailFactory.activity}</div>
                {detailFactory.address && <div className="col-span-2"><span className="text-slate-500">العنوان:</span> {detailFactory.address}</div>}
                <div><span className="text-slate-500">التسجيل:</span> {detailFactory.created_at ? new Date(detailFactory.created_at).toLocaleString('ar-SA') : '—'}</div>
                <div><span className="text-slate-500">{t('admin.table.status')}:</span> {detailFactory.is_active ? <Badge className="bg-emerald-600">{t('common.active')}</Badge> : <Badge variant="destructive">{t('common.inactive')}</Badge>}</div>
              </div>
              <div className="border-t pt-3">
                <div className="font-bold mb-2 flex items-center gap-1"><FileText size={14}/> تقارير SDS الخاصة بالمصنع ({detailFactory.sds?.length || 0})</div>
                {(detailFactory.sds || []).length === 0 ? (
                  <div className="text-xs text-slate-500 text-center py-6 bg-slate-50 rounded">لا توجد تقارير لهذا المصنع</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto pr-1">
                    {(detailFactory.sds || []).map((s) => (
                      <Link
                        key={s.id}
                        to={`/admin/sds/pdf/${s.id}`}
                        onClick={() => setDetailFactory(null)}
                        className="block group"
                        data-testid={`admin-factory-sds-card-${s.id}`}
                      >
                        <div className="bg-white rounded-lg border hover:shadow-lg hover:border-[#1a4d80] transition-all overflow-hidden flex flex-col">
                          <div className="relative bg-slate-100" style={{ aspectRatio: '210 / 297' }}>
                            {s.thumbnail ? (
                              <img
                                src={s.thumbnail}
                                alt={s.title}
                                className="absolute inset-0 w-full h-full object-cover object-top"
                              />
                            ) : (
                              <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 p-2">
                                <FileText size={32} className="mb-1" />
                                <span className="text-[10px] text-center">معاينة غير متوفرة</span>
                              </div>
                            )}
                            <div className="absolute inset-0 bg-[#1a4d80]/0 group-hover:bg-[#1a4d80]/60 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <div className="bg-white rounded-full px-3 py-1.5 shadow-lg text-xs font-bold text-[#1a4d80] flex items-center gap-1">
                                <Download size={12}/> عرض PDF
                              </div>
                            </div>
                            <div className="absolute top-1.5 right-1.5">
                              <RiskPill risk={s.risk}/>
                            </div>
                          </div>
                          <div className="p-2">
                            <div className="font-bold text-xs text-slate-900 truncate">{s.title}</div>
                            <div className="text-[10px] text-slate-500 flex items-center justify-between mt-0.5">
                              <span>{s.chemicalsCount} مواد</span>
                              <span>{s.updated_at ? new Date(s.updated_at).toLocaleDateString('ar-SA') : '—'}</span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailFactory(null)}>إغلاق</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SDS Detail Dialog */}
      <Dialog open={!!detailSDS} onOpenChange={(o) => !o && setDetailSDS(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle>تفاصيل التقرير</DialogTitle>
            <DialogDescription>معلومات المصنع وقائمة المواد الكيميائية في التقرير.</DialogDescription>
          </DialogHeader>
          {detailSDS && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 bg-slate-50 p-3 rounded">
                <div><span className="text-slate-500">المصنع:</span> <b>{detailSDS.factoryName}</b></div>
                <div><span className="text-slate-500">المدينة:</span> {detailSDS.factoryCity}</div>
                <div><span className="text-slate-500">آخر تعديل:</span> {detailSDS.updated_at ? new Date(detailSDS.updated_at).toLocaleString('ar-SA') : '—'}</div>
                <div><span className="text-slate-500">الخطورة:</span> <RiskPill risk={detailSDS.risk}/></div>
              </div>
              <div>
                <div className="font-bold mb-2">المواد ({(detailSDS.data?.chemicals || []).length})</div>
                <div className="space-y-1">
                  {(detailSDS.data?.chemicals || []).map((c, i) => (
                    <div key={i} className="border rounded p-2 text-xs">
                      <b>{c.name || `مادة ${i + 1}`}</b>
                      {(c.unNumber || c.un) && <span className="mr-2 text-slate-500"> • UN {c.unNumber || c.un}</span>}
                      {(c.casNumber || c.casNo || c.cas) && <span className="mr-2 text-slate-500"> • CAS {c.casNumber || c.casNo || c.cas}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            {detailSDS && (
              <Link to={`/admin/sds/edit/${detailSDS.id}`}>
                <Button className="bg-emerald-600 hover:bg-emerald-700"><Edit size={14} className="ml-1"/> فتح للتعديل</Button>
              </Link>
            )}
            <Button variant="outline" onClick={() => setDetailSDS(null)}>إغلاق</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Expanded KPI Detail Dialog */}
      <Dialog open={!!expandedStat} onOpenChange={(o) => !o && setExpandedStat(null)}>
        <DialogContent dir="rtl" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {expandedStat && stats && (() => {
            const closeBtn = () => setExpandedStat(null);
            // Renderer per stat key
            if (expandedStat === 'factories') {
              const total = stats.totalFactories || 0;
              const cities = stats.cities || [];
              const activities = stats.activities || [];
              return (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Building2 size={18} className="text-[#1a4d80]"/> تفاصيل المصانع ({total})</DialogTitle>
                    <DialogDescription>نظرة شاملة على توزيع المصانع حسب المدينة والنشاط</DialogDescription>
                  </DialogHeader>
                  <div className="grid md:grid-cols-2 gap-4 mt-2">
                    <Card className="p-3 bg-gradient-to-br from-blue-50 to-white border-blue-200/60">
                      <div className="font-bold text-[#1a4d80] mb-2 flex items-center gap-1 text-sm"><MapPin size={14}/> حسب المدينة</div>
                      <BarsList items={cities} accent="bg-[#1a4d80]" total={total} />
                    </Card>
                    <Card className="p-3 bg-gradient-to-br from-indigo-50 to-white border-indigo-200/60">
                      <div className="font-bold text-indigo-700 mb-2 flex items-center gap-1 text-sm"><Activity size={14}/> حسب النشاط</div>
                      <BarsList items={activities} accent="bg-indigo-600" total={total} />
                    </Card>
                  </div>
                  <DialogFooter><Button variant="outline" onClick={closeBtn}>إغلاق</Button><Button onClick={() => { closeBtn(); setTab('factories'); }} className="bg-[#1a4d80]">عرض الجدول</Button></DialogFooter>
                </>
              );
            }
            if (expandedStat === 'active') {
              const active = stats.activeFactories || 0;
              const inactive = stats.inactiveFactories || 0;
              const total = active + inactive || 1;
              const activePct = Math.round((active / total) * 100);
              return (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Power size={18} className="text-emerald-600"/> حالة تفعيل المصانع</DialogTitle>
                    <DialogDescription>توزيع المصانع النشطة وغير النشطة</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3 mt-2">
                    <div className="flex items-center gap-3">
                      <div className="bg-emerald-100 text-emerald-700 rounded-xl w-14 h-14 flex items-center justify-center"><Power size={26}/></div>
                      <div className="flex-1">
                        <div className="text-xs text-slate-500">مصانع نشطة</div>
                        <div className="text-2xl font-extrabold text-emerald-700">{active} <span className="text-sm font-normal text-slate-500">({activePct}%)</span></div>
                      </div>
                    </div>
                    <div className="h-3 rounded-full bg-slate-100 overflow-hidden shadow-inner">
                      <div className="h-full bg-gradient-to-l from-emerald-500 to-emerald-400 transition-all" style={{ width: `${activePct}%` }} />
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="bg-red-100 text-red-700 rounded-xl w-14 h-14 flex items-center justify-center"><PowerOff size={26}/></div>
                      <div className="flex-1">
                        <div className="text-xs text-slate-500">مصانع غير نشطة</div>
                        <div className="text-2xl font-extrabold text-red-700">{inactive} <span className="text-sm font-normal text-slate-500">({100 - activePct}%)</span></div>
                      </div>
                    </div>
                  </div>
                  <DialogFooter><Button variant="outline" onClick={closeBtn}>إغلاق</Button><Button onClick={() => { closeBtn(); setTab('factories'); }} className="bg-[#1a4d80]">عرض المصانع</Button></DialogFooter>
                </>
              );
            }
            if (expandedStat === 'sds') {
              const buckets = stats.riskBuckets || { low: 0, medium: 0, high: 0 };
              const total = stats.totalSDS || 0;
              const pct = (n) => total ? Math.round((n/total)*100) : 0;
              return (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><FileText size={18} className="text-indigo-600"/> تفاصيل التقارير ({total})</DialogTitle>
                    <DialogDescription>توزيع التقارير حسب مستوى الخطورة</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3 mt-2">
                    {[
                      { key: 'high', label: 'عالي الخطورة', color: 'bg-red-600', light: 'bg-red-50', text: 'text-red-700', count: buckets.high },
                      { key: 'medium', label: 'متوسط الخطورة', color: 'bg-amber-500', light: 'bg-amber-50', text: 'text-amber-700', count: buckets.medium },
                      { key: 'low', label: 'منخفض الخطورة', color: 'bg-emerald-600', light: 'bg-emerald-50', text: 'text-emerald-700', count: buckets.low },
                    ].map((row) => (
                      <div key={row.key} className={`${row.light} rounded-lg p-3 flex items-center gap-3 border`}>
                        <ShieldAlert className={row.text} size={22}/>
                        <div className="flex-1">
                          <div className="flex justify-between text-sm font-semibold">
                            <span className={row.text}>{row.label}</span>
                            <span className="text-slate-700">{row.count} ({pct(row.count)}%)</span>
                          </div>
                          <div className="h-2 mt-1 rounded-full bg-white/70 overflow-hidden">
                            <div className={`h-full ${row.color} transition-all`} style={{ width: `${pct(row.count)}%` }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <DialogFooter><Button variant="outline" onClick={closeBtn}>إغلاق</Button><Button onClick={() => { closeBtn(); setTab('sds'); }} className="bg-[#1a4d80]">عرض التقارير</Button></DialogFooter>
                </>
              );
            }
            if (expandedStat === 'chemicals') {
              const hz = stats.hazardCounts || {};
              const HAZARDS = [
                { key: 'toxic', label: 'سمّي', icon: Skull, color: 'text-purple-700', bg: 'bg-purple-50', fill: 'bg-purple-600' },
                { key: 'flammable', label: 'قابل للاشتعال', icon: Flame, color: 'text-orange-700', bg: 'bg-orange-50', fill: 'bg-orange-600' },
                { key: 'corrosive', label: 'أكّال', icon: Droplet, color: 'text-rose-700', bg: 'bg-rose-50', fill: 'bg-rose-600' },
                { key: 'oxidizer', label: 'مؤكسد', icon: AlertTriangle, color: 'text-yellow-700', bg: 'bg-yellow-50', fill: 'bg-yellow-600' },
                { key: 'explosive', label: 'متفجر', icon: Bomb, color: 'text-red-700', bg: 'bg-red-50', fill: 'bg-red-600' },
                { key: 'environment', label: 'خطر بيئي', icon: Leaf, color: 'text-emerald-700', bg: 'bg-emerald-50', fill: 'bg-emerald-600' },
                { key: 'health-hazard', label: 'خطر صحي', icon: AlertTriangle, color: 'text-pink-700', bg: 'bg-pink-50', fill: 'bg-pink-600' },
              ];
              const maxCount = Math.max(1, ...HAZARDS.map((h) => hz[h.key] || 0));
              return (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><FlaskConical size={18} className="text-amber-600"/> تفاصيل المواد الكيميائية ({stats.totalChemicals || 0})</DialogTitle>
                    <DialogDescription>توزيع المخاطر حسب التصنيف العالمي GHS</DialogDescription>
                  </DialogHeader>
                  <div className="grid sm:grid-cols-2 gap-2 mt-2">
                    {HAZARDS.map((h) => {
                      const n = hz[h.key] || 0;
                      const w = Math.round((n / maxCount) * 100);
                      const Ic = h.icon;
                      return (
                        <div key={h.key} className={`${h.bg} rounded-lg p-2.5 border flex items-center gap-2`}>
                          <Ic className={h.color} size={20}/>
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between text-xs font-semibold">
                              <span className={h.color}>{h.label}</span>
                              <span className="text-slate-700 font-bold">{n}</span>
                            </div>
                            <div className="h-1.5 mt-1 rounded-full bg-white/70 overflow-hidden">
                              <div className={`h-full ${h.fill} transition-all`} style={{ width: `${w}%` }} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <DialogFooter><Button variant="outline" onClick={closeBtn}>إغلاق</Button></DialogFooter>
                </>
              );
            }
            // risk-high / medium / low
            if (expandedStat.startsWith('risk-')) {
              const level = expandedStat.replace('risk-', ''); // high|medium|low
              const map = {
                high: { label: 'عالية', color: 'bg-red-600', text: 'text-red-700' },
                medium: { label: 'متوسطة', color: 'bg-amber-500', text: 'text-amber-700' },
                low: { label: 'منخفضة', color: 'bg-emerald-600', text: 'text-emerald-700' },
              };
              const conf = map[level];
              const list = (notif?.highRisk || []).filter(() => level === 'high'); // only high has direct list
              return (
                <>
                  <DialogHeader>
                    <DialogTitle className={`flex items-center gap-2 ${conf.text}`}><ShieldAlert size={18}/> التقارير {conf.label} الخطورة ({stats.riskBuckets[level] || 0})</DialogTitle>
                    <DialogDescription>قائمة آخر التقارير ذات الخطورة {conf.label}</DialogDescription>
                  </DialogHeader>
                  {level === 'high' && list.length > 0 ? (
                    <div className="space-y-1.5 mt-2 max-h-[55vh] overflow-y-auto">
                      {list.map((s) => (
                        <Card key={s.id} className="p-2 flex items-center justify-between bg-red-50 border-red-200">
                          <div className="min-w-0">
                            <div className="font-bold text-sm text-red-900 truncate">{s.title || 'تقرير'}</div>
                            <div className="text-xs text-red-700 truncate">{s.factoryName}</div>
                          </div>
                          <Badge className={`${conf.color} text-white`}>{s.score}</Badge>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-6 text-sm">
                      لا توجد قائمة مفصّلة • انتقل لتبويب التقارير لاستعراض الكل وتصفيتها حسب الخطورة
                    </div>
                  )}
                  <DialogFooter><Button variant="outline" onClick={closeBtn}>إغلاق</Button><Button onClick={() => { closeBtn(); setTab('sds'); }} className="bg-[#1a4d80]">عرض التقارير</Button></DialogFooter>
                </>
              );
            }
            return null;
          })()}
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={!!pwdResetUser} onOpenChange={(o) => { if (!o) { setPwdResetUser(null); setPwdValue(''); setPwdConfirm(''); } }}>
        <DialogContent dir="rtl" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound size={18}/> إعادة تعيين كلمة المرور</DialogTitle>
            <DialogDescription>
              تعيين كلمة مرور جديدة للحساب: <b>{pwdResetUser?.label}</b>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">كلمة المرور الجديدة (6 أحرف على الأقل)</label>
              <Input type="password" value={pwdValue} onChange={(e) => setPwdValue(e.target.value)} placeholder="••••••••" data-testid="pwd-new-input" autoComplete="new-password"/>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">تأكيد كلمة المرور</label>
              <Input type="password" value={pwdConfirm} onChange={(e) => setPwdConfirm(e.target.value)} placeholder="••••••••" data-testid="pwd-confirm-input" autoComplete="new-password"/>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setPwdResetUser(null); setPwdValue(''); setPwdConfirm(''); }}>إلغاء</Button>
            <Button onClick={submitPasswordReset} disabled={pwdSubmitting} className="bg-[#1a4d80] hover:bg-[#143d68]" data-testid="pwd-submit-btn">
              {pwdSubmitting ? <Loader2 className="animate-spin ml-1" size={14}/> : <KeyRound size={14} className="ml-1"/>} تعيين
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Manager Dialog */}
      <Dialog open={roleMgrOpen} onOpenChange={setRoleMgrOpen}>
        <DialogContent dir="rtl" className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck size={18}/> إدارة المشرفين</DialogTitle>
            <DialogDescription>
              قائمة المستخدمين الذين يتمتعون بصلاحيات الإدارة. لترقية مصنع إلى مشرف، استخدم زر <ShieldCheck size={12} className="inline mx-1 text-indigo-700"/> في صف المصنع.
            </DialogDescription>
          </DialogHeader>
          {roleLoading ? (
            <div className="flex items-center gap-2 text-slate-600 py-6"><Loader2 className="animate-spin" size={16}/> جارٍ التحميل...</div>
          ) : adminUsers.length === 0 ? (
            <div className="text-center text-slate-500 py-6">لا يوجد مشرفون</div>
          ) : (
            <div className="space-y-2">
              {adminUsers.map((u) => {
                const isMe = u.id === user?.id;
                return (
                  <Card key={u.id} className="p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid={`admin-row-${u.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="bg-indigo-100 text-indigo-700 rounded-lg w-10 h-10 flex items-center justify-center flex-shrink-0">
                        <UserCog size={18}/>
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold text-slate-900 truncate">{u.factoryName || 'مشرف'}{isMe && <Badge className="mr-2 bg-emerald-600 text-[10px]">أنت</Badge>}</div>
                        <div className="text-xs text-slate-600 truncate">{u.email}</div>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => { setPwdResetUser({ id: u.id, label: u.email }); setPwdValue(''); setPwdConfirm(''); }} className="text-amber-700 text-xs" data-testid={`admin-reset-${u.id}`}>
                        <KeyRound size={12} className="ml-1"/> كلمة المرور
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => demoteToFactory(u)} disabled={isMe} className="text-red-600 text-xs" data-testid={`demote-admin-${u.id}`}>
                        <PowerOff size={12} className="ml-1"/> تنزيل إلى مصنع
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleMgrOpen(false)}>إغلاق</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog — replaces native window.confirm (which can be silently blocked in iframe/preview) */}
      <AlertDialog open={!!confirmState} onOpenChange={(o) => { if (!o) closeConfirm(); }}>
        <AlertDialogContent dir={i18n.language === 'en' ? 'ltr' : 'rtl'} data-testid="admin-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmState?.title || 'تأكيد'}</AlertDialogTitle>
            <AlertDialogDescription>{confirmState?.description || 'هل أنت متأكد؟'}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-confirm-cancel">إلغاء</AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-confirm-ok"
              onClick={() => {
                const fn = confirmState?.onConfirm;
                closeConfirm();
                if (typeof fn === 'function') fn();
              }}
              className={confirmState?.danger ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' : ''}
            >
              {confirmState?.confirmLabel || 'تأكيد'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SiteFooter/>
    </div>
  );
}
